import { useMutation, useQuery } from '@apollo/client';
import { IConfirmActionModal } from 'components/site/modals/ModalWrapper/ModalContent/contents/ConfirmActionModal';
import { EModal } from 'components/site/modals/ModalWrapper/ModalContent/useGetModalProperties';
import { SelectValue } from 'elements/minute/sidebar/sidebarComponents/atomic/SelectValue';
import { G_DELETE_PROGRAM_ITEM, G_DELETE_STORY } from 'graphql/mutation/guideMutation';
import {
    GuideQueryView,
    GuideQueryViewVariables
} from 'graphql/query/__generated__/GuideQueryView';
import { GuidesForCurrentUserQuery } from 'graphql/query/__generated__/GuidesForCurrentUserQuery';
import { CURRENT_USER_GUIDES_QUERY, GUIDE_QUERY_VIEW } from 'graphql/query/guideQuery';
import { EToastType } from 'libs/types/site/IClient';
import { userMessage } from 'libs/utils/logic/ui/uiLogic';
import { S_PageStyle } from 'pages/page/PageStyle';
import { useContext, useEffect, useState } from 'react';
import { CtxModal } from 'session/ctxModal';
import styled from 'styled-components/macro';

interface Props {
    className?: string;
}

export const DeleteDetailsPage = (props: Props): JSX.Element => {
    const ctxModal = useContext(CtxModal);
    const [guideId, setGuideId] = useState<string>('');
    const [selectedDetailsIds, setSelectedDetailsIds] = useState<string[]>([]);
    const [selectedProgramIds, setSelectedProgramIds] = useState<string[]>([]);

    const allGuides = useQuery<GuidesForCurrentUserQuery>(CURRENT_USER_GUIDES_QUERY);

    const guideQuery = useQuery<GuideQueryView, GuideQueryViewVariables>(GUIDE_QUERY_VIEW, {
        variables: {
            guideIdOrPath: guideId
        },
        nextFetchPolicy: 'network-only',
        skip: !guideId
    });

    const [deleteDetail] = useMutation(G_DELETE_STORY, {
        onCompleted: () => {
            guideQuery.refetch();
            setSelectedDetailsIds([]);
            userMessage(EToastType.SUCCESS, 'detail gone');
        }
    });

    const [deleteProgram] = useMutation(G_DELETE_PROGRAM_ITEM, {
        onCompleted: () => {
            guideQuery.refetch();
            setSelectedProgramIds([]);
            userMessage(EToastType.SUCCESS, 'program gone');
        }
    });

    useEffect(() => {
        guideQuery.refetch();
        setSelectedDetailsIds([]);
        setSelectedProgramIds([]);
    }, [guideId]);

    async function deleteDetails() {
        for (const id of selectedDetailsIds) {
            await deleteDetail({
                variables: {
                    storyId: id,
                    guideId
                }
            });
        }
    }

    async function deletePrograms() {
        for (const id of selectedProgramIds) {
            await deleteProgram({
                variables: {
                    guideProgramItemId: id,
                    guideId
                }
            });
        }
    }

    const guideView = guideQuery.data?.guideView;

    return (
        <S_ImportPage className={props.className}>
            <S_Label>
                <S_LabelText>Guide:</S_LabelText>
                <S_SelectValue
                    options={allGuides.data?.currentUserGuides?.map((e) => ({
                        label: e.title,
                        value: e.guideId
                    }))}
                    onChange={(e) => {
                        setGuideId(e.value);
                    }}
                />
            </S_Label>
            {guideQuery.data?.guideView?.guideId === guideId && guideId && (
                <>
                    {/* Details */}
                    <S_Control>
                        <S_Head>
                            <span>Selected details: {selectedDetailsIds.length}</span>
                            <button
                                onClick={() => {
                                    setSelectedDetailsIds(
                                        guideView.stories.map((story) => story.storyId)
                                    );
                                }}
                            >
                                Select all
                            </button>
                            <button
                                onClick={() => {
                                    setSelectedDetailsIds([]);
                                }}
                            >
                                Deselect all
                            </button>
                            {selectedDetailsIds.length > 0 && (
                                <button
                                    onClick={() => {
                                        ctxModal.setModal({
                                            type: EModal.CONFIRM,
                                            data: {
                                                text:
                                                    'Delete details? ' + selectedDetailsIds.length,
                                                onConfirm: () => {
                                                    deleteDetails();
                                                }
                                            } as IConfirmActionModal
                                        });
                                    }}
                                >
                                    Delete details
                                </button>
                            )}
                        </S_Head>
                        <S_Items>
                            {guideView.stories?.map((story) => {
                                return (
                                    <S_Item
                                        key={story.storyId}
                                        $selected={selectedDetailsIds.includes(story.storyId)}
                                        onClick={() => {
                                            if (selectedDetailsIds.includes(story.storyId)) {
                                                setSelectedDetailsIds(
                                                    selectedDetailsIds.filter(
                                                        (id) => id !== story.storyId
                                                    )
                                                );
                                            } else {
                                                setSelectedDetailsIds([
                                                    ...selectedDetailsIds,
                                                    story.storyId
                                                ]);
                                            }
                                        }}
                                    >
                                        {story.header.title}
                                    </S_Item>
                                );
                            })}
                        </S_Items>
                    </S_Control>
                    {/* Program */}
                    <S_Control>
                        <S_Head>
                            <span>Selected items: {selectedProgramIds.length}</span>
                            <button
                                onClick={() => {
                                    setSelectedProgramIds(
                                        guideView.programItems.map(
                                            (program) => program.guideProgramItemId
                                        )
                                    );
                                }}
                            >
                                Select all
                            </button>
                            <button
                                onClick={() => {
                                    setSelectedProgramIds([]);
                                }}
                            >
                                Deselect all
                            </button>
                            {selectedProgramIds.length > 0 && (
                                <button
                                    onClick={() => {
                                        ctxModal.setModal({
                                            type: EModal.CONFIRM,
                                            data: {
                                                text:
                                                    'Delete program? ' + selectedProgramIds.length,
                                                onConfirm: () => {
                                                    deletePrograms();
                                                }
                                            } as IConfirmActionModal
                                        });
                                    }}
                                >
                                    Delete programs
                                </button>
                            )}
                        </S_Head>
                        <S_Items>
                            {guideView.programItems?.map((program) => {
                                return (
                                    <S_Item
                                        key={program.guideProgramItemId}
                                        $selected={selectedProgramIds.includes(
                                            program.guideProgramItemId
                                        )}
                                        onClick={() => {
                                            if (
                                                selectedProgramIds.includes(
                                                    program.guideProgramItemId
                                                )
                                            ) {
                                                setSelectedProgramIds(
                                                    selectedProgramIds.filter(
                                                        (id) => id !== program.guideProgramItemId
                                                    )
                                                );
                                            } else {
                                                setSelectedProgramIds([
                                                    ...selectedProgramIds,
                                                    program.guideProgramItemId
                                                ]);
                                            }
                                        }}
                                    >
                                        {program.title ?? program.guideProgramItemId}
                                    </S_Item>
                                );
                            })}
                        </S_Items>
                    </S_Control>
                </>
            )}
        </S_ImportPage>
    );
};

const S_ImportPage = styled(S_PageStyle)`
    padding: 30px;
    padding-right: 550px;
`;

const S_Label = styled.label`
    display: flex;
    flex-direction: row;
    gap: 50px;
    margin-bottom: 20px;
`;

const S_LabelText = styled.div`
    width: 100px;
`;

const S_SelectValue = styled(SelectValue)`
    width: 200px;
`;

const S_Items = styled.div`
    display: flex;
    flex-direction: column;
    gap: 10px;
`;

const S_Item = styled.div<{ $selected: boolean }>`
    padding: 10px;
    border: 1px solid black;
    cursor: pointer;
    background-color: ${(props) => (props.$selected ? 'lightblue' : 'white')};
`;

const S_Control = styled.div`
    display: flex;
    flex-direction: column;
    gap: 10px;
`;

const S_Head = styled.div`
    display: flex;
    flex-direction: row;
    gap: 30px;
`;
